import React, { useState, useEffect } from 'react';
import { MARKETS } from '../config/markets';
import { submitWorkerApplication } from '../lib/supabase';
import COLORS from '../config/colors';

const TRADE_OPTIONS = [
  { id: 'decks', label: 'Custom Decks' },
  { id: 'concrete', label: 'Concrete Flatwork' },
  { id: 'painting', label: 'Interior & Exterior Painting' },
  { id: 'roofing', label: 'Roofing' },
];

const OFFERS = [
  { icon: '💰', title: 'Pay 15–20% above local GC rates in USD', desc: 'That is company payroll, not an on-chain token.' },
  { icon: '📍', title: 'Local work in your market', desc: 'Decks, concrete, painting, roofing. Dispatch is UltraLife Trades, not a public job board yet.' },
  { icon: '📋', title: 'Application on file', desc: 'Submitting this form writes your record in our worker database. That is the system of record today.' },
  { icon: '🪪', title: 'Optional on-chain identity later', desc: 'Cardano preprod only, and only if Ryan signs the mint. See identity below.' },
];

const STEPS = [
  { num: '01', title: 'Apply', desc: 'Apply on this form.' },
  { num: '02', title: 'We review', desc: 'Background check, 1–2 business days.' },
  { num: '03', title: 'Paid starter job', desc: 'A paid starter job in USD to verify skill.' },
  { num: '04', title: 'Dispatch', desc: 'Work through UltraLife dispatch in your market.' },
];

export default function WorkerApplication() {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    market: '',
    trades: [],
    yearsExperience: '',
    hasTools: '',
    hasVehicle: '',
    hasInsurance: '',
    desiredRate: '',
    experienceDesc: '',
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Work with UltraLife Trades';
    return () => { document.title = prev; };
  }, []);

  const markets = Object.values(MARKETS).sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );

  function setField(key, value) {
    setForm(f => ({ ...f, [key]: value }));
  }

  function toggleTrade(id) {
    setForm(f => ({
      ...f,
      trades: f.trades.includes(id)
        ? f.trades.filter(t => t !== id)
        : [...f.trades, id],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.trades.length === 0) {
      setError('Please select at least one trade.');
      return;
    }
    setSubmitting(true);
    setError(null);

    const { error: submitError } = await submitWorkerApplication({
      ...form,
      photoCount: photos.length,
    });

    if (submitError) {
      setError(submitError);
      setSubmitting(false);
    } else {
      setSubmitted(true);
    }
  }

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 10,
    border: '1px solid rgba(155,142,130,0.3)',
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: 'none',
    background: COLORS.cream,
    boxSizing: 'border-box',
    color: COLORS.bark,
    transition: 'border 0.2s',
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    color: COLORS.stone,
    display: 'block',
    marginBottom: 6,
  };

  const sectionH2 = {
    fontSize: 26, fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: COLORS.bark, margin: '0 0 6px',
  };

  if (submitted) {
    return (
      <div style={{
        minHeight: '100vh', background: COLORS.cream,
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <div style={{ maxWidth: 520, textAlign: 'center' }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(74,124,89,0.12)', border: '2px solid rgba(74,124,89,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px', fontSize: 28, color: COLORS.pine, fontWeight: 700,
          }}>
            ✓
          </div>
          <div style={{ fontSize: 30, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.bark, marginBottom: 12 }}>
            Application Received!
          </div>
          <div style={{ fontSize: 15, color: COLORS.stone, lineHeight: 1.7, marginBottom: 32 }}>
            Thanks, {form.fullName.split(' ')[0]}! We'll review your application and reach out within 1–2 business days to discuss next steps.
          </div>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '12px 28px', borderRadius: 10, border: 'none',
              background: COLORS.honey, color: COLORS.white,
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: '0 2px 8px rgba(196,145,59,0.3)',
            }}
          >
            Back to Quote Builder
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: COLORS.cream, fontFamily: "'DM Sans', sans-serif" }}>
      <header style={{
        background: COLORS.bark, padding: '0 24px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
      }}>
        <button
          onClick={() => window.location.href = '/'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.snow, letterSpacing: 0.5 }}>
            UltraLife Trades
          </div>
        </button>
        <button
          onClick={() => window.location.href = '/'}
          style={{
            padding: '8px 16px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent', color: 'rgba(245,240,235,0.75)',
            fontSize: 13, fontWeight: 500, cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          ← Get a Quote
        </button>
      </header>

      <div style={{ background: COLORS.bark, padding: '64px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: 3, color: COLORS.honey, marginBottom: 16,
          }}>
            For contractors and trade workers
          </div>
          <h1 style={{
            fontSize: 42, fontWeight: 700,
            fontFamily: "'Playfair Display', serif",
            color: COLORS.snow, margin: '0 0 16px', lineHeight: 1.15,
          }}>
            Join the UltraLife<br />Trades crew
          </h1>
          <p style={{
            fontSize: 18, color: 'rgba(245,240,235,0.7)',
            lineHeight: 1.6, margin: '0 auto', maxWidth: 520,
          }}>
            Apply here. We pay 15–20% above local GC rates in USD, through UltraLife Trades, not as crypto on a blockchain.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '60px 24px 80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: 56, alignItems: 'start' }}>

          <div>
            <section style={{ marginBottom: 56 }}>
              <h2 style={sectionH2}>What We Offer</h2>
              <p style={{ fontSize: 14, color: COLORS.stone, margin: '0 0 28px', lineHeight: 1.6 }}>
                Built for serious tradespeople who want steady, quality local work.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {OFFERS.map((offer, i) => (
                  <div key={i} style={{
                    background: COLORS.white, borderRadius: 16, padding: 20,
                    border: '1px solid rgba(155,142,130,0.12)',
                    boxShadow: '0 1px 4px rgba(44,24,16,0.06)',
                  }}>
                    <div style={{ fontSize: 26, marginBottom: 10 }}>{offer.icon}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.bark, marginBottom: 5, lineHeight: 1.3 }}>
                      {offer.title}
                    </div>
                    <div style={{ fontSize: 13, color: COLORS.stone, lineHeight: 1.55 }}>
                      {offer.desc}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ marginBottom: 56 }}>
              <h2 style={sectionH2}>How It Works</h2>
              <p style={{ fontSize: 14, color: COLORS.stone, margin: '0 0 28px', lineHeight: 1.6 }}>
                Apply, we review, then a paid starter job in USD.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {STEPS.map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: 20, paddingBottom: i < STEPS.length - 1 ? 28 : 0, position: 'relative' }}>
                    {i < STEPS.length - 1 && (
                      <div style={{
                        position: 'absolute', left: 21, top: 44, bottom: 0,
                        width: 1, background: 'rgba(196,145,59,0.2)',
                      }} />
                    )}
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%',
                      background: 'rgba(196,145,59,0.08)',
                      border: '2px solid rgba(196,145,59,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.honey, letterSpacing: 0.5 }}>
                        {step.num}
                      </span>
                    </div>
                    <div style={{ paddingTop: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.bark, marginBottom: 4 }}>
                        {step.title}
                      </div>
                      <div style={{ fontSize: 13, color: COLORS.stone, lineHeight: 1.55 }}>
                        {step.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ marginBottom: 56 }}>
              <h2 style={sectionH2}>Identity: Basic vs Standard</h2>
              <p style={{ fontSize: 14, color: COLORS.stone, margin: '0 0 16px', lineHeight: 1.6 }}>
                Identity mint is in test. We will not mint a pNFT from this form.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{
                  background: COLORS.white, borderRadius: 16, padding: 20,
                  border: '1px solid rgba(155,142,130,0.12)',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.bark, marginBottom: 8 }}>
                    Basic (the only mint that exists today)
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.stone, lineHeight: 1.6 }}>
                    A Basic pNFT is a Cardano preprod identity: wallet signature, one token in a test wallet. It lets us attach an on-chain name to a person. It cannot transact protocol tokens, cannot post a job, and cannot bid. We will not mint one from this form. If we ever mint Basic for you, Ryan signs that transaction. Preprod is a test network. It is not mainnet.
                  </div>
                </div>
                <div style={{
                  background: COLORS.white, borderRadius: 16, padding: 20,
                  border: '1px solid rgba(155,142,130,0.12)',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.bark, marginBottom: 8 }}>
                    Standard (not offered)
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.stone, lineHeight: 1.6 }}>
                    The protocol says Standard is DNA-verified and is the level that can transact and bid. We are not running a DNA oracle. We will not upgrade anyone to Standard by putting a hash in a transaction. Applying here does not let you bid on-chain.
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 style={sectionH2}>What a labor record actually writes</h2>
              <p style={{ fontSize: 14, color: COLORS.stone, margin: '0 0 16px', lineHeight: 1.6 }}>
                Labor records are not live for crews. work_auction is not a live crew job.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{
                  background: COLORS.white, borderRadius: 16, padding: 20,
                  border: '1px solid rgba(155,142,130,0.12)',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.bark, marginBottom: 8 }}>On apply (this page)</div>
                  <div style={{ fontSize: 13, color: COLORS.stone, lineHeight: 1.6 }}>
                    A row in our worker database: name, email, phone, market, trades, experience, tools/vehicle/insurance, desired USD rate, notes. Status starts at applied. No Cardano transaction. No rating. No hours.
                  </div>
                </div>
                <div style={{
                  background: COLORS.white, borderRadius: 16, padding: 20,
                  border: '1px solid rgba(155,142,130,0.12)',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.bark, marginBottom: 8 }}>On a finished job (today)</div>
                  <div style={{ fontSize: 13, color: COLORS.stone, lineHeight: 1.6 }}>
                    Off-chain company records and USD pay. Not an on-chain hours ledger.
                  </div>
                </div>
                <div style={{
                  background: COLORS.white, borderRadius: 16, padding: 20,
                  border: '1px solid rgba(155,142,130,0.12)',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.bark, marginBottom: 8 }}>On-chain (not live for crews)</div>
                  <div style={{ fontSize: 13, color: COLORS.stone, lineHeight: 1.6 }}>
                    work_auction and records validators are deployed on preprod. They do not yet record a real job, hours, or a star rating for a tradesperson.
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div style={{
            background: COLORS.white, borderRadius: 20, padding: 32,
            boxShadow: '0 4px 24px rgba(44,24,16,0.08)',
            border: '1px solid rgba(155,142,130,0.12)',
            position: 'sticky', top: 84,
          }}>
            <h2 style={{
              fontSize: 20, fontWeight: 700,
              fontFamily: "'Playfair Display', serif",
              color: COLORS.bark, margin: '0 0 4px',
            }}>
              Apply to Join
            </h2>
            <p style={{ fontSize: 13, color: COLORS.stone, margin: '0 0 24px', lineHeight: 1.5 }}>
              Takes about 3 minutes. We'll be in touch quickly.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Full Name *</label>
                <input
                  type="text" required
                  value={form.fullName}
                  onChange={e => setField('fullName', e.target.value)}
                  placeholder="Jane Smith"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Email *</label>
                <input
                  type="email" required
                  value={form.email}
                  onChange={e => setField('email', e.target.value)}
                  placeholder="jane@example.com"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Phone *</label>
                <input
                  type="tel" required
                  value={form.phone}
                  onChange={e => setField('phone', e.target.value)}
                  placeholder="(555) 555-0100"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Your City / Market *</label>
                <select
                  required
                  value={form.market}
                  onChange={e => setField('market', e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="">Select your market...</option>
                  {markets.map(m => (
                    <option key={m.slug} value={m.slug}>
                      {m.displayName}, {m.state}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Trades You Do *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {TRADE_OPTIONS.map(t => (
                    <label key={t.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      cursor: 'pointer', padding: '9px 12px', borderRadius: 8,
                      border: `1px solid ${form.trades.includes(t.id) ? 'rgba(196,145,59,0.4)' : 'rgba(155,142,130,0.2)'}`,
                      background: form.trades.includes(t.id) ? 'rgba(196,145,59,0.06)' : 'transparent',
                      transition: 'all 0.15s',
                    }}>
                      <input
                        type="checkbox"
                        checked={form.trades.includes(t.id)}
                        onChange={() => toggleTrade(t.id)}
                        style={{ accentColor: COLORS.honey, width: 15, height: 15, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.bark }}>{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Years of Experience</label>
                <input
                  type="number" min="0" max="50"
                  value={form.yearsExperience}
                  onChange={e => setField('yearsExperience', e.target.value)}
                  placeholder="e.g. 5"
                  style={inputStyle}
                />
              </div>

              {[
                { key: 'hasTools', label: 'Do you have your own tools?' },
                { key: 'hasVehicle', label: 'Do you have a vehicle?' },
                { key: 'hasInsurance', label: 'Do you have liability insurance?' },
              ].map(({ key, label }) => (
                <div key={key} style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>{label}</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['yes', 'no'].map(opt => (
                      <button
                        key={opt} type="button"
                        onClick={() => setField(key, opt)}
                        style={{
                          flex: 1, padding: '10px', borderRadius: 8,
                          border: `1px solid ${form[key] === opt ? COLORS.honey : 'rgba(155,142,130,0.3)'}`,
                          background: form[key] === opt ? 'rgba(196,145,59,0.1)' : 'transparent',
                          color: form[key] === opt ? COLORS.honey : COLORS.stone,
                          fontSize: 13, fontWeight: form[key] === opt ? 600 : 400,
                          cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                          textTransform: 'capitalize', transition: 'all 0.15s',
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Desired Hourly Rate ($)</label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: 14, top: '50%',
                    transform: 'translateY(-50%)', color: COLORS.stone, fontSize: 14,
                  }}>$</span>
                  <input
                    type="number" min="15" max="250"
                    value={form.desiredRate}
                    onChange={e => setField('desiredRate', e.target.value)}
                    placeholder="45"
                    style={{ ...inputStyle, paddingLeft: 28 }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Tell Us About Your Experience</label>
                <textarea
                  value={form.experienceDesc}
                  onChange={e => setField('experienceDesc', e.target.value)}
                  placeholder="Describe your background, specialties, biggest projects, certifications, etc."
                  rows={4}
                  style={{
                    ...inputStyle, resize: 'vertical', minHeight: 90,
                    lineHeight: 1.5,
                  }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Photos of Previous Work (Optional)</label>
                <label style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  padding: '18px 16px', borderRadius: 10,
                  border: '2px dashed rgba(155,142,130,0.3)',
                  background: COLORS.cream, cursor: 'pointer', transition: 'all 0.15s',
                }}>
                  <span style={{ fontSize: 22 }}>📷</span>
                  <span style={{ fontSize: 13, color: COLORS.stone }}>
                    {photos.length > 0
                      ? `${photos.length} photo${photos.length > 1 ? 's' : ''} selected`
                      : 'Click to upload photos of your work'}
                  </span>
                  <input
                    type="file" multiple accept="image/*"
                    style={{ display: 'none' }}
                    onChange={e => setPhotos(Array.from(e.target.files))}
                  />
                </label>
              </div>

              {error && (
                <div style={{
                  color: COLORS.red, fontSize: 13, marginBottom: 16,
                  padding: '10px 14px', background: 'rgba(196,75,59,0.06)',
                  borderRadius: 8, border: '1px solid rgba(196,75,59,0.15)',
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit" disabled={submitting}
                style={{
                  width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                  background: submitting ? COLORS.stone : COLORS.honey,
                  color: COLORS.white, fontSize: 15, fontWeight: 600,
                  cursor: submitting ? 'wait' : 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                  transition: 'all 0.2s',
                  boxShadow: submitting ? 'none' : '0 2px 8px rgba(196,145,59,0.35)',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Application →'}
              </button>

              <p style={{ fontSize: 11, color: COLORS.stone, textAlign: 'center', marginTop: 12, lineHeight: 1.6 }}>
                By applying you agree to a background check. We do not mint a blockchain identity from this form. Pay is USD through UltraLife Trades. Cardano preprod identity, if any, is a separate step Ryan signs.
              </p>
            </form>
          </div>
        </div>
      </div>

      <footer style={{
        padding: '20px 24px', textAlign: 'center',
        fontSize: 12, color: COLORS.stone,
        borderTop: '1px solid rgba(155,142,130,0.1)',
      }}>
        © {new Date().getFullYear()} UltraLife Trades · ultralifetrades.com
      </footer>
    </div>
  );
}
